/** Route guards. RequireAuth gates a subtree behind a valid session (and optional persona);
 *  RootRedirect sends "/" to the persona home when signed in, or to /login when not. */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { Persona } from "@talenthop/shared";
import { useAuth } from "../lib/AuthContext.js";

/** Where each persona lands after auth. */
export function personaHome(persona: Persona): string {
  return persona === "admin" ? "/admin" : persona === "user" ? "/app" : "/interview";
}

export function RequireAuth({
  persona,
  children,
}: {
  /** if set, callers whose persona differs are bounced to their own home. */
  persona?: Persona;
  children: ReactNode;
}): ReactNode {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="auth-loading">Loading your workspace…</div>;
  }
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (persona && session.persona !== persona) {
    return <Navigate to={personaHome(session.persona)} replace />;
  }
  return <>{children}</>;
}

/** "/" → persona home if authed, else the login screen. */
export function RootRedirect(): ReactNode {
  const { session } = useAuth();
  return <Navigate to={session ? personaHome(session.persona) : "/login"} replace />;
}
